import { keys } from 'lodash'
import i18n from '@/lang/i18n'
import grossingSampleService, {
    GROSSING_SAMPLE_STATES,
    VIRTUAL_SHELF_OPTIONS_GROSSING_SAMPLE
} from '@/modules/grossingModule/services/grossingSampleService'
import { STATUS_OPTIONS_WASTE_SAMPLE } from '@/modules/wasteModule/services/WasteSampleService'
import { caseState } from '@/api/resources/entityState/case-state'
import { portionState } from '@/api/resources/entityState/portion-state'
import { subportionState } from '@/api/resources/entityState/subportion-state'
import { sampleState } from '@/api/resources/entityState/sample-state'
import { requestState } from '@/api/resources/entityState/request-state'
import { assayState } from '@/api/resources/entityState/assay-state'
import { shipmentEntityState } from '@/api/resources/entityState/shipment-entity-state'
import { shipmentRequestState } from '@/api/resources/entityState/shipment-request-state'
import StudyRequestStatusRenderer from '@/cellRenderers/studyRequest/StudyRequestStatusRenderer.vue'
import CaseStatusRenderer from '@/cellRenderers/registry/CaseStatusRenderer.vue'
import AssayStatusRenderer from '@/cellRenderers/laboratory/AssayStatusRenderer.vue'
import SampleShelfRenderer from '@/cellRenderers/grossing/SampleShelfRenderer.vue'
import PriorityRenderer from '@/cellRenderers/studyRequest/PriorityRenderer.vue'
import IncidentRenderer from '@/cellRenderers/common/IncidentRenderer.vue'
import AssociatedCasesRenderer from '@/cellRenderers/common/AssociatedCasesRenderer.vue'
import AttachmentCountRenderer from '@/cellRenderers/common/AttachmentCountRenderer.vue'
import TranslatedEntityRenderer from '@/cellRenderers/common/TranslatedEntityRenderer.vue'
import BackendTranslatedEntityPlainTextRenderer
    from '@/cellRenderers/incidents/BackendTranslatedEntityPlainTextRenderer.vue'
import { COREENTITY_VALUES, INVALID_VALUE } from '@/api/resources/incident/incidentServices'
import {
    SHIPMENT_COREENTITY_VALUES,
    SHIPMENT_INVALID_VALUE
} from '@/modules/shipments-module/old/constants/shipmentCoreEntities'
import { loadedSeverities } from '@/api/resources/incident/incidentSeverityServices'
import { genderKeys, genderMap } from '@/api/resources/GenderResource'
import CommonSampleStatusRenderer from '@/cellRenderers/common/CommonSampleStatusRenderer.vue'
import CommonPortionStatusRenderer from '@/cellRenderers/common/CommonPortionStatusRenderer.vue'
import CommonSubportionStatusRenderer from '@/cellRenderers/common/CommonSubportionStatusRenderer.vue'
import ReadIndicadorRenderer from '@/cellRenderers/common/ReadIndicadorRenderer.vue'
import CheckIcon from '@/cellRenderers/common/CheckIcon.vue'
import FlagsIcons from '@/cellRenderers/common/FlagsIcons.vue'
import PriorityColorIcons from '@/cellRenderers/common/PriorityColorIcons.vue'
import AmendmentIcon from '@/cellRenderers/common/AmendmentIcon.vue'
import CommonSetFilterRenderer from '@/cellRenderers/common/CommonSetFilterRenderer.vue'
import CommonStatusBadge from '@/cellRenderers/common/CommonStatusBadge.vue'
import CommonCheckIcon from '@/cellRenderers/common/CommonCheckIcon.vue'
import ReadIndicatorBadge from '@/components/common/ReadIndicatorBadge.vue'
import laboratorySampleServices, {
    STATUS_OPTIONS_LABORATORY_SAMPLE
} from '@/modules/laboratoryModule/services/laboratorySampleServices'
import { NOTIFICATION_ENTITIES } from '@/components/NavBar/notifications/services/notificationEntityEnum'
import {
    AgGridColumnFilter,
    AgGridColumnType,
    AgGridOptions,
    AgGridRowModelType,
    AgGridServerSideStoreType
} from '@/shared/models/ag-grid.model'
import { TextNormalization } from '@/shared/util/text-normalization'
import { mountPatientAgeString } from '@/utils/fieldTransformations'
import { VtGridTextFormatter } from '@/grid/vt-grid-text-formatter'
import { VtGridValueFormatter } from '@/grid/vt-grid-value-formatter'
import { VtGridValues } from '@/grid/vt-grid-values'
import { VtGridComparator } from '@/grid/vt-grid-comparator'
import { EntityStatusState } from '@/store/root/root-store.model'
import {
    ColDef,
    ICellRendererParams,
    KeyCreatorParams,
    SetFilterValuesFuncParams,
    ValueGetterParams
} from '@ag-grid-enterprise/all-modules'
import { ISetFilterCellRendererParams } from '@ag-grid-community/core/dist/cjs/rendering/cellRenderers/iCellRenderer'
import { store } from '@/store/store'
import { SimpleListResponse } from '@/api/services/common/simple-list/common-simple-list.dto'
import HasUnauthorizedBillableConceptsIcon
    from '@/cellRenderers/common/has-unauthorized-billable-concepts-icon/has-unauthorized-billable-concepts-icon.vue'
import HasUnauthorizedBillableConceptsIconRenderer
    from '@/cellRenderers/common/has-unauthorized-billable-concepts-icon/renderer/has-unauthorized-billable-concepts-icon-renderer.vue'
import buildIconHeaderTemplate from '@/grid/buildIconHeaderTemplate'
import {
    configurationLocalAccountingRatesModalBasicFormInvoiceListValues
} from '@/modules/configurationModule/detail/local-accounting/billable/rates/components/rates-modal-basic-form/configuration-local-accounting-billable-rate-modal-basic-form.model'

export const getEntityStatusStateColumnGridOptions =
    (states: EntityStatusState[]): ColDef => ({
        keyCreator: ({ value }: KeyCreatorParams): string => value?.code,
        suppressSizeToFit: true,
        flex: 1,
        minWidth: 140,
        cellClass: 'badge-grid-cell',
        cellRendererFramework: TranslatedEntityRenderer,
        cellRendererParams: {
            values: states
        },
        filterParams: {
            newRowsAction: 'keep',
            cellRendererFramework: CommonSetFilterRenderer,
            cellRendererParams: {
                states: states,
                component: CommonStatusBadge
            },
            textFormatter: (from: string): string => VtGridTextFormatter.entityStatusStateFormatter(from, states),
            comparator: (codeA: string, codeB: string): number => VtGridComparator.entityStatusStateComparator(codeA, codeB, states),
            values: (params: SetFilterValuesFuncParams): void => VtGridValues.entityStatusStateValues(params, states),
            debounceMs: 1000
        }
    })

const getEntitySimpleListColumnGridOptions =
    (list: SimpleListResponse[]): ColDef => ({
        keyCreator: ({ value }: KeyCreatorParams): string => value?.id,
        suppressSizeToFit: true,
        width: 110,
        cellRendererFramework: BackendTranslatedEntityPlainTextRenderer,
        cellRendererParams: {
            values: list,
            invalid: INVALID_VALUE
        },
        filterParams: {
            newRowsAction: 'keep',
            cellRendererFramework: BackendTranslatedEntityPlainTextRenderer,
            cellRendererParams: {
                values: list,
                invalid: INVALID_VALUE
            },
            values: list.map(item => item.id),
            comparator: (idA: string, idB: string): number => VtGridComparator.entitySimpleListComparator(idA, idB, list),
            textFormatter: (from: string): string => VtGridTextFormatter.entitySimpleListFormatter(from, list),
            debounceMs: 1000
        }
    })

export const getEnumColumnGridOptions =
    <T>(enumValues: T[], code: keyof T, name: keyof T): ColDef => ({
        filter: true,
        filterParams: {
            debounceMs: 100,
            values: enumValues.map(enumValue => enumValue[code]),
            cellRenderer: ({ value }: ISetFilterCellRendererParams): string => {
                const enumValue = enumValues.find(ev => ev[code] === value)
                return enumValue ? enumValue[name] : value
            }
        }
    })

export const getEnumTranslationKeyColumnGridOptions =
    <T>(enumValues: T[], codeFieldName: keyof T, translationKeyFieldName: keyof T): ColDef => ({
        filter: true,
        cellRenderer: ({ value }: ICellRendererParams): string => i18n.tc(value),
        filterParams: {
            debounceMs: 100,
            values: enumValues.map(enumValue => enumValue[codeFieldName]),
            cellRenderer: ({ value }: ISetFilterCellRendererParams): string => {
                const enumValue = enumValues.find(ev => ev[codeFieldName] === value)
                if (enumValue) {
                    const key = enumValue[translationKeyFieldName] as unknown as string
                    return i18n.tc(key)
                }
                return value
            }
        }
    })

export const baseGridOptions =
        (): AgGridOptions => ({
            suppressPropertyNamesCheck: true,
            overlayNoRowsTemplate: i18n.tc('global.noResults'),
            rowModelType: AgGridRowModelType.SERVER_SIDE,
            serverSideStoreType: AgGridServerSideStoreType.PARTIAL,
            pagination: true,
            paginationAutoPageSize: true,
            allowDragFromColumnsToolPanel: true,
            rowSelection: 'multiple',
            suppressRowClickSelection: true,
            suppressDragLeaveHidesColumns: true,
            tooltipShowDelay: 200,
            localeTextFunc: (key: string): string => i18n.tc(`component.agGrid.${key}`),
            defaultColDef: {
                sortable: true,
                filter: true,
                resizable: true,
                menuTabs: ['filterMenuTab', 'generalMenuTab'],
                minWidth: 30,
                filterParams: { newRowsAction: 'keep' }
            },
            columnTypes: {
                [AgGridColumnType.MASTER_DETAIL]: {
                    cellRenderer: 'agGroupCellRenderer',
                    valueGetter: () => '',
                    hide: false,
                    width: 30,
                    pinned: 'left',
                    lockPinned: true,
                    sortable: false,
                    suppressMovable: true,
                    filter: false,
                    resizable: false,
                    suppressSizeToFit: true,
                    suppressMenu: true,
                    suppressColumnsToolPanel: true,
                    maxWidth: 40
                },
                [AgGridColumnType.HAS_UNAUTHORIZED_BILLABLE_CONCEPTS]: {
                    headerName: i18n.tc('global.valueConceptsAuthorization'),
                    headerTooltip: i18n.tc('global.valueConceptsAuthorization'),
                    headerComponentParams: {
                        template: buildIconHeaderTemplate('fas fa-calculator')
                    },
                    sortable: true,
                    cellRendererFramework: HasUnauthorizedBillableConceptsIconRenderer,
                    filterParams: {
                        suppressMiniFilter: true,
                        cellRendererFramework: CommonSetFilterRenderer,
                        cellRendererParams: {
                            states: [{ code: 'true' }, { code: 'false' }],
                            component: HasUnauthorizedBillableConceptsIcon
                        },
                        values: ['true', 'false'],
                        debounceMs: 1000
                    }
                },
                [AgGridColumnType.DIACRITICS_INSENSITIVE_TEXT_FOR_CODE_DESCRIPTION_OR_STRING]: {
                    filter: AgGridColumnFilter.TEXT,
                    filterParams: {
                        textFormatter: (from: string): string =>
                            TextNormalization.commonSetFilterTextFormatter(
                                from,
                                option => option ? `${option.code} | ${option.description}` : '')
                    }
                },
                [AgGridColumnType.CUSTOM_CHECKBOX]: {
                    checkboxSelection: true,
                    colId: 'checkbox-column',
                    headerComponent: 'customCheckboxHeader',
                    hide: false,
                    minWidth: 40,
                    pinned: 'left',
                    lockPinned: true,
                    sortable: false,
                    suppressMovable: true,
                    filter: false,
                    resizable: false,
                    suppressSizeToFit: true,
                    suppressMenu: true,
                    suppressColumnsToolPanel: true,
                    maxWidth: 40
                },
                [AgGridColumnType.PRIORITY]: {
                    keyCreator: ({ value }: KeyCreatorParams): string => value?.level,
                    minWidth: 100,
                    suppressSizeToFit: true,
                    cellRendererFramework: PriorityRenderer,
                    filter: 'agSetColumnFilter',
                    filterParams: {
                        suppressMiniFilter: true,
                        cellRendererFramework: PriorityRenderer,
                        values: VtGridValues.priorityValues,
                        debounceMs: 1000
                    }
                },
                [AgGridColumnType.COMMON_CASE_STATUS]: {
                    ...getEntityStatusStateColumnGridOptions(caseState.states),
                    cellRendererFramework: CaseStatusRenderer
                },
                [AgGridColumnType.GROSSING_SAMPLE_STATUS]: {
                    ...getEntityStatusStateColumnGridOptions(grossingSampleService.getFullSampleStates()),
                    filterParams: {
                        ...getEntityStatusStateColumnGridOptions(grossingSampleService.getFullSampleStates()).filterParams,
                        values: GROSSING_SAMPLE_STATES
                    }
                },
                [AgGridColumnType.LABORATORY_SAMPLE_STATUS]: {
                    ...getEntityStatusStateColumnGridOptions(laboratorySampleServices.getFullSampleStates()),
                    filterParams: {
                        ...getEntityStatusStateColumnGridOptions(laboratorySampleServices.getFullSampleStates()).filterParams,
                        values: STATUS_OPTIONS_LABORATORY_SAMPLE
                    }
                },
                [AgGridColumnType.COMMON_SAMPLE_STATUS]: {
                    ...getEntityStatusStateColumnGridOptions(sampleState.states),
                    cellRendererFramework: CommonSampleStatusRenderer
                },
                [AgGridColumnType.WASTE_SAMPLE_STATUS]: {
                    ...getEntityStatusStateColumnGridOptions(sampleState.states),
                    filterParams: {
                        ...getEntityStatusStateColumnGridOptions(sampleState.states).filterParams,
                        values: STATUS_OPTIONS_WASTE_SAMPLE
                    }
                },
                [AgGridColumnType.COMMON_REQUEST_STATUS]: {
                    ...getEntityStatusStateColumnGridOptions(requestState.statesWithoutDraft),
                    cellRendererFramework: StudyRequestStatusRenderer
                },
                [AgGridColumnType.COMMON_PORTION_STATUS]: {
                    ...getEntityStatusStateColumnGridOptions(portionState.states),
                    cellRendererFramework: CommonPortionStatusRenderer
                },
                [AgGridColumnType.COMMON_ASSAY_STATUS]: {
                    ...getEntityStatusStateColumnGridOptions(assayState.states),
                    cellRendererFramework: AssayStatusRenderer,
                    filterParams: {
                        ...getEntityStatusStateColumnGridOptions(assayState.states).filterParams,
                        suppressMiniFilter: true
                    }
                },
                [AgGridColumnType.SAMPLE_VIRTUAL_SHELF]: {
                    suppressSizeToFit: true,
                    width: 110,
                    cellClass: 'badge-grid-cell',
                    cellRendererFramework: SampleShelfRenderer,
                    filterParams: {
                        suppressMiniFilter: true,
                        cellRendererFramework: SampleShelfRenderer,
                        values: VIRTUAL_SHELF_OPTIONS_GROSSING_SAMPLE.map(virtualShelf => virtualShelf.key),
                        debounceMs: 1000
                    }
                },
                [AgGridColumnType.COMMON_SUBPORTION_STATUS]: {
                    ...getEntityStatusStateColumnGridOptions(subportionState.states),
                    cellRendererFramework: CommonSubportionStatusRenderer
                },
                [AgGridColumnType.INCIDENT]: {
                    suppressSizeToFit: true,
                    width: 65,
                    filter: false,
                    sortable: false,
                    cellRendererFramework: IncidentRenderer
                },
                [AgGridColumnType.ASSOCIATED_CASES]: {
                    suppressSizeToFit: true,
                    minWidth: 140,
                    filter: false,
                    sortable: false,
                    cellRendererFramework: AssociatedCasesRenderer
                },
                [AgGridColumnType.ATTACHMENT_COUNT]: {
                    suppressSizeToFit: true,
                    width: 65,
                    filter: false,
                    sortable: false,
                    cellRendererFramework: AttachmentCountRenderer
                },
                [AgGridColumnType.CHECK_ICON]: {
                    sortable: true,
                    cellRendererFramework: CheckIcon,
                    filterParams: {
                        suppressMiniFilter: true,
                        cellRendererFramework: CommonSetFilterRenderer,
                        cellRendererParams: {
                            states: [{ code: 'true' }, { code: 'false' }],
                            component: CommonCheckIcon
                        },
                        values: ['true', 'false'],
                        debounceMs: 1000
                    }
                },
                [AgGridColumnType.HIS]: {
                    sortable: true,
                    cellRendererFramework: CheckIcon,
                    valueGetter: (params: ValueGetterParams) => !params.node?.data.patId,
                    filterParams: {
                        suppressMiniFilter: true,
                        cellRendererFramework: CommonSetFilterRenderer,
                        cellRendererParams: {
                            states: [{ code: 'true' }, { code: 'false' }],
                            component: CommonCheckIcon
                        },
                        values: ['true', 'false'],
                        debounceMs: 1000
                    }
                },
                [AgGridColumnType.FLAGS_RENDERER]: {
                    suppressSizeToFit: true,
                    width: 65,
                    sortable: true,
                    cellRendererFramework: FlagsIcons
                },
                [AgGridColumnType.PRIORITY_COLOR_RENDERER]: {
                    suppressSizeToFit: true,
                    width: 65,
                    sortable: true,
                    cellRendererFramework: PriorityColorIcons
                },
                [AgGridColumnType.AMENDMENT_RENDERER]: {
                    suppressSizeToFit: true,
                    width: 65,
                    sortable: true,
                    cellRendererFramework: AmendmentIcon,
                    filterParams: {
                        suppressMiniFilter: true,
                        cellRendererFramework: CommonSetFilterRenderer,
                        cellRendererParams: {
                            states: [{ code: 'true' }, { code: 'false' }],
                            component: CommonCheckIcon
                        },
                        values: ['true', 'false'],
                        debounceMs: 1000
                    }
                },
                [AgGridColumnType.INCIDENT_CORE_ENTITY]: {
                    keyCreator: ({ value }: KeyCreatorParams): string => value?.code,
                    suppressSizeToFit: true,
                    cellClass: 'badge-grid-cell',
                    cellRendererFramework: TranslatedEntityRenderer,
                    cellRendererParams: {
                        values: Object.values(COREENTITY_VALUES),
                        invalid: INVALID_VALUE
                    },
                    filterParams: {
                        cellRendererFramework: CommonSetFilterRenderer,
                        cellRendererParams: {
                            states: Object.values(COREENTITY_VALUES),
                            component: CommonStatusBadge
                        },
                        textFormatter: (from: string): string => VtGridTextFormatter.entityStatusStateFormatter(from, Object.values(COREENTITY_VALUES) as EntityStatusState[]),
                        values: keys(COREENTITY_VALUES),
                        debounceMs: 1000
                    }
                },
                [AgGridColumnType.NOTIFICATION_ENTITY_TYPE]: {
                    suppressSizeToFit: true,
                    width: 110,
                    cellClass: 'badge-grid-cell',
                    cellRendererFramework: TranslatedEntityRenderer,
                    cellRendererParams: {
                        values: Object.values(NOTIFICATION_ENTITIES),
                        invalid: INVALID_VALUE
                    },
                    filterParams: {
                        cellRendererFramework: CommonSetFilterRenderer,
                        cellRendererParams: {
                            states: Object.values(NOTIFICATION_ENTITIES),
                            component: CommonStatusBadge
                        },
                        textFormatter: (from: string): string => VtGridTextFormatter.entityStatusStateFormatter(from, Object.values(NOTIFICATION_ENTITIES) as EntityStatusState[]),
                        values: keys(NOTIFICATION_ENTITIES),
                        debounceMs: 1000
                    }
                },
                [AgGridColumnType.INCIDENT_SEVERITY]: {
                    keyCreator: ({ value }: KeyCreatorParams): string => value?.id,
                    suppressSizeToFit: true,
                    cellRenderer: ({ value }: ICellRendererParams): string => value?.name,
                    filterParams: {
                        suppressMiniFilter: true,
                        cellRenderer: ({ value }: ICellRendererParams): string => {
                            return value === i18n.tc('component.agGrid.selectAll')
                                ? value
                                : loadedSeverities[value].name
                        },
                        textFormatter: (from: any): string => {
                            if (loadedSeverities[from]) {
                                return loadedSeverities[from].name
                            }
                            return from
                        },
                        values: Object.values(loadedSeverities).map(severity => severity.id),
                        debounceMs: 1000
                    }
                },
                [AgGridColumnType.INCIDENT_ORIGIN]: getEntitySimpleListColumnGridOptions(store.getters.laboratoryIncidents(store.getters.labId)?.origins ?? []),
                [AgGridColumnType.INCIDENT_TYPE]: getEntitySimpleListColumnGridOptions(store.getters.laboratoryIncidents(store.getters.labId)?.types ?? []),
                [AgGridColumnType.CENTERS]: {
                    filter: 'agSetColumnFilter',
                    filterParams: {
                        debounceMs: 100,
                        values: VtGridValues.centerValues
                    }
                },
                [AgGridColumnType.DEPARTMENTS]: {
                    filter: 'agSetColumnFilter',
                    minWidth: 150,
                    flex: 1,
                    filterParams: {
                        debounceMs: 100,
                        values: VtGridValues.departmentValues
                    }
                },
                [AgGridColumnType.LABORATORIES]: {
                    filter: 'agSetColumnFilter',
                    filterParams: {
                        debounceMs: 100,
                        values: VtGridValues.laboratoryValues
                    }
                },
                [AgGridColumnType.BILLING_COPAYMENT_TYPE]: {
                    ...getEntitySimpleListColumnGridOptions(configurationLocalAccountingRatesModalBasicFormInvoiceListValues()),
                    keyCreator: ({ value }: KeyCreatorParams): string => value?.code,
                    cellRenderer: ({ value }: ICellRendererParams) => value?.translatedName
                },
                [AgGridColumnType.GENDERS]: {
                    width: 110,
                    suppressSizeToFit: true,
                    cellRenderer: ({ value }: ICellRendererParams): string => value,
                    filterParams: {
                        suppressMiniFilter: true,
                        cellRenderer: ({ value }: ICellRendererParams): string => {
                            return value === i18n.tc('component.agGrid.selectAll')
                                ? value
                                : (genderMap as any)[value].name
                        },
                        cellRendererParams: {
                            values: genderMap
                        },
                        values: genderKeys,
                        debounceMs: 1000
                    }
                },
                [AgGridColumnType.TEXT]: {
                    filter: AgGridColumnFilter.TEXT
                },
                [AgGridColumnType.NUMBER]: {
                    valueFormatter: VtGridValueFormatter.numberFormatter,
                    filter: AgGridColumnFilter.NUMBER
                },
                [AgGridColumnType.DECIMAL]: {
                    valueFormatter: VtGridValueFormatter.decimalFormatter,
                    filter: AgGridColumnFilter.NUMBER
                },
                [AgGridColumnType.DATE]: {
                    valueFormatter: VtGridValueFormatter.dateFormatter,
                    filter: AgGridColumnFilter.DATE,
                    filterParams: { comparator: VtGridComparator.dateComparator, newRowsAction: 'keep' },
                    width: 110
                },
                [AgGridColumnType.DATETIME]: {
                    valueFormatter: VtGridValueFormatter.dateTimeFormatter,
                    filter: AgGridColumnFilter.DATE,
                    filterParams: {
                        comparator: VtGridComparator.dateTimeComparator,
                        type: 'datetime',
                        newRowsAction: 'keep'
                    },
                    width: 140
                },
                [AgGridColumnType.FIXED_WIDTH]: {
                    suppressSizeToFit: true
                },
                [AgGridColumnType.SUPPRESS_ALL]: {
                    sortable: false,
                    suppressMovable: true,
                    filter: false,
                    resizable: false,
                    suppressSizeToFit: true,
                    suppressMenu: true,
                    suppressColumnsToolPanel: true,
                    maxWidth: 40
                },
                [AgGridColumnType.READ_INDICATOR]: {
                    suppressSizeToFit: true,
                    width: 110,
                    cellRendererFramework: ReadIndicadorRenderer,
                    filterParams: {
                        suppressMiniFilter: true,
                        cellRendererFramework: CommonSetFilterRenderer,
                        cellRendererParams: {
                            states: [{ code: 'true' }, { code: 'false' }],
                            component: ReadIndicatorBadge
                        },
                        values: ['true', 'false'],
                        debounceMs: 1000
                    }
                },
                [AgGridColumnType.SHIPMENT_CORE_ENTITY]: {
                    keyCreator: ({ value }: KeyCreatorParams): string => value?.code,
                    suppressSizeToFit: true,
                    width: 110,
                    cellRendererFramework: TranslatedEntityRenderer,
                    cellClass: 'badge-grid-cell',
                    cellRendererParams: {
                        values: Object.values(SHIPMENT_COREENTITY_VALUES),
                        invalid: SHIPMENT_INVALID_VALUE
                    },
                    filterParams: {
                        cellRendererFramework: CommonSetFilterRenderer,
                        cellRendererParams: {
                            states: Object.values(SHIPMENT_COREENTITY_VALUES),
                            component: CommonStatusBadge
                        },
                        textFormatter: (from: string): string => VtGridTextFormatter.entityStatusStateFormatter(from, Object.values(SHIPMENT_COREENTITY_VALUES) as EntityStatusState[]),
                        values: keys(SHIPMENT_COREENTITY_VALUES),
                        debounceMs: 1000
                    }
                },
                [AgGridColumnType.SHIPMENT_ENTITY_STATUS]: getEntityStatusStateColumnGridOptions(shipmentEntityState.states),
                [AgGridColumnType.SHIPMENT_REQUEST_STATUS]: getEntityStatusStateColumnGridOptions(shipmentRequestState.states),
                [AgGridColumnType.PATIENT_AGE]: {
                    filter: AgGridColumnFilter.NUMBER,
                    cellRenderer: ({ data }: ICellRendererParams): string => mountPatientAgeString(data?.agePatient),
                    minWidth: 80
                }
            }
        })
